import React, {useState} from 'react';
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import Page from "components/Page";
import {Table, TableBody, TableData, TableHead, TableHeader, TableRow} from "components/Table";
import {invalidate, prefetch} from "services/query-client";
import {PaperAirplaneIcon as PaperAirplaneOutlineIcon} from "@heroicons/react/outline";
import {formattedDateTime} from "services/date";
import Button from "components/Button";
import {BeakerIcon, EyeIcon, PaperAirplaneIcon, PencilIcon, PlusIcon, TrashIcon} from "@heroicons/react/solid";
import Dropdown from "components/Dropdown";
import {Message} from "types/Message";
import {useNotifications} from "context/NotificationContext";
import {useNavigate} from "react-router-dom";
import DotBadge from "components/DotBadge";
import MessageStatus from "types/MessageStatus";
import DeleteMessage from "pages/Message/Delete";
import TestMessage from "../Test";
import useSorting from "../../../hooks/useSorting";
import {SortDirection} from "../../../types/SortDirection";

// Map message status to badge color
const statusColors: Record<MessageStatus, number> = {
  [MessageStatus.DRAFT]: DotBadge.colors.GRAY,
  [MessageStatus.SCHEDULED]: DotBadge.colors.BLUE,
  [MessageStatus.SENDING]: DotBadge.colors.YELLOW,
  [MessageStatus.SENT]: DotBadge.colors.GREEN,
}

function MessageList() {
  const [page, setPage] = useState<number>(1);
  const {currentSort, queryString: sort, sortBy} = useSorting();
  const {data: messages, isLoading} = useQuery(
    ['messages.all', {page, sort}],
    () => ngage.messages.filtered({page, sort}),
    {keepPreviousData: true}
  )

  const [deletingMessage, setDeletingMessage] = useState<Message | null>(null);
  const [testingMessage, setTestingMessage] = useState<Message | null>(null);
  const {mutateAsync: sendMessage} = useMutation(ngage.messages.send)

  const {notify} = useNotifications()
  const navigate = useNavigate()

  // Prepare action handlers
  // TODO: Add a confirmation modal here? It could say something like "You are about to send this to X people, are you sure?"
  async function handleSendMessage(message: Message) {
    try {
      await sendMessage(message.id)
      notify("success", "Message Sent", `${message.subject} has been sent ${message.audience_size ? `to ${message.audience_size} people` : ''}`)

      await invalidate(`messages.${message.id}`)
      await invalidate('messages.all')
    } catch (error) {
      notify("error", "Message Not Sent", `${message.subject} could not be sent`)
    }
  }

  // Prefetch next and previous pages
  prefetch('messages.all', {sort, page}, ngage.messages.filtered)

  // Prepare page actions
  const actions = <Button icon={PlusIcon} onClick={() => navigate('/messages/new')}>New</Button>

  return (
    <Page
      title="Messages"
      subTitle='Engaging users with push notifications and email'
      actions={actions}
      breadcrumbs={{'Engagement': '/', 'Messages': '/messages'}}
    >
      <Table paginationData={messages} setPage={setPage}>
        <TableHead>
          <TableRow>
            <TableHeader
              sortable
              sortDirection={currentSort.subject ?? SortDirection.NONE}
              onSort={direction => sortBy('subject', direction)}
            >
              Subject
            </TableHeader>
            <TableHeader>Offer(s)</TableHeader>
            <TableHeader
              sortable
              sortDirection={currentSort.last_sent_at ?? SortDirection.NONE}
              onSort={direction => sortBy('last_sent_at', direction)}
            >
              Last Sent
            </TableHeader>
            <TableHeader
              sortable
              sortDirection={currentSort.due_at ?? SortDirection.NONE}
              onSort={direction => sortBy('due_at', direction)}
            >
              Next Due
            </TableHeader>
            <TableHeader
              sortable
              sortDirection={currentSort.status ?? SortDirection.NONE}
              onSort={direction => sortBy('status', direction)}
            >
              Status
            </TableHeader>
            <TableHeader alignment={TableHeader.alignments.RIGHT}>Reach</TableHeader>
            <TableHeader/>
          </TableRow>
        </TableHead>

        <TableBody isLoading={isLoading}>

          {messages && messages.data.length < 1 ?
            <TableRow index={0}>
              <TableData colSpan={7}>
                <div className="py-4 flex flex-col justify-center items-center">
                  <PaperAirplaneOutlineIcon className="h-8 w-8 text-gray-400 mb-2"/>
                  <span className="text-sm font-medium text-gray-600">No messages</span>
                </div>
              </TableData>
            </TableRow>
            : messages?.data.map((message, index) => (
              <TableRow key={message.id} index={index} highlight>
                <TableData>
                  <div className="flex">
                    <div className="items-center absolute h-full left-[0.35rem] top-0 flex duration-200 transition-opacity group-hover:opacity-100 opacity-0">
                      {/* TODO: Convert to component */}
                      <Dropdown position="left">
                        {[
                          {name: 'View', icon: EyeIcon, onClick: () => navigate(`/messages/${message.id}`)},
                          {name: 'Test...', icon: BeakerIcon, onClick: () => setTestingMessage(message)},
                          {
                            name: 'Send',
                            icon: PaperAirplaneIcon,
                            onClick: () => handleSendMessage(message),
                            disabled: message.status === MessageStatus.SENDING
                          },
                          {
                            name: 'Edit',
                            icon: PencilIcon,
                            onClick: () => navigate(`/messages/${message.id}/edit`),
                            disabled: !!message.last_sent_at
                          },
                          {name: 'Delete...', icon: TrashIcon, onClick: () => setDeletingMessage(message)}
                        ]}
                      </Dropdown>
                    </div>

                    <div className="flex group-hover:translate-x-1 transition-all">
                      <PaperAirplaneOutlineIcon className="h-5 w-5 mr-2 text-gray-500"/> {message.subject}
                    </div>
                  </div>
                </TableData>
                <TableData>{message.offer_count}</TableData>
                <TableData>{formattedDateTime(message.last_sent_at) || 'Never'}</TableData>
                <TableData>{formattedDateTime(message.due_at) || 'Never'}</TableData>
                <TableData transform={TableData.transforms.CAPITALIZE}>
                  <DotBadge color={statusColors[message.status]} text={message.status} />
                </TableData>
                <TableData alignment={TableData.alignments.RIGHT}>{message.reach}</TableData>
                <TableData alignment={TableData.alignments.RIGHT}>
                  {/* TODO: Convert to component */}
                  <Dropdown>
                    {[
                      {name: 'View', icon: EyeIcon, onClick: () => navigate(`/messages/${message.id}`)},
                      {name: 'Test...', icon: BeakerIcon, onClick: () => setTestingMessage(message)},
                      {
                        name: 'Send',
                        icon: PaperAirplaneIcon,
                        onClick: () => handleSendMessage(message),
                        disabled: message.status === MessageStatus.SENDING
                      },
                      {
                        name: 'Edit',
                        icon: PencilIcon,
                        onClick: () => navigate(`/messages/${message.id}/edit`),
                        disabled: !!message.last_sent_at
                      },
                      {name: 'Delete...', icon: TrashIcon, onClick: () => setDeletingMessage(message)}
                    ]}
                  </Dropdown>
                </TableData>
              </TableRow>
            ))}

        </TableBody>
      </Table>

      {/* Test Confirmation modal */}
      <TestMessage
        show={!!testingMessage}
        message={testingMessage}
        onCancel={() => setTestingMessage(null)}
        onSend={() => setTestingMessage(null)}
      />

      {/* Delete confirmation modal */}
      <DeleteMessage
        show={!!deletingMessage}
        message={deletingMessage}
        onCancel={() => setDeletingMessage(null)}
        onDelete={() => setDeletingMessage(null)}
        onDeleteFail={() => setDeletingMessage(null)}
      />
    </Page>
  );
}

export default MessageList;